import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ShoppingCart, Package, Users, Receipt, BarChart3, 
  Smartphone, Shield, Clock, CreditCard, Truck,
  FileText, Settings, X, MessageCircle, Phone, Mail,
  MapPin, ChevronRight, Check, Star, Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/plans`);
      setPlans(response.data);
    } catch (error) {
      // Fallback plans if API fails
      setPlans([
        { id: 1, name: 'Free Trial', price_inr: 0, validity_days: 14, max_products: 50, max_bills_per_month: 100, features: ['50 Products', '100 Bills/Month', 'Basic Reports', 'Email Support'] },
        { id: 2, name: 'Starter', price_inr: 299, validity_days: 30, max_products: 200, max_bills_per_month: 500, features: ['200 Products', '500 Bills/Month', 'All Reports', 'Priority Support'] },
        { id: 3, name: 'Professional', price_inr: 599, validity_days: 30, max_products: 1000, max_bills_per_month: 2000, features: ['1000 Products', 'Unlimited Bills', 'Advanced Reports', '24/7 Support', 'Multi-Cashier'] },
      ]);
    }
  };

  const features = [
    { icon: ShoppingCart, title: 'Quick Billing', desc: 'Fast POS billing with barcode scanning support' },
    { icon: Package, title: 'Inventory Management', desc: 'Track stock levels with low stock alerts' },
    { icon: Users, title: 'Customer Management', desc: 'Maintain customer records and credit ledger' },
    { icon: Receipt, title: 'GST Compliant', desc: 'Generate GST invoices with tax breakdowns' },
    { icon: BarChart3, title: 'Reports & Analytics', desc: 'Daily sales, expenses & profit reports' },
    { icon: Smartphone, title: 'Mobile Friendly', desc: 'Works on tablets and mobile devices' },
    { icon: CreditCard, title: 'Multi-Payment', desc: 'Cash, UPI, Card & Credit payments' },
    { icon: Truck, title: 'Supplier Ledger', desc: 'Track supplier payments and GRN entries' },
    { icon: Clock, title: 'Cash Register', desc: 'Daily cash tracking with detailed reports' },
    { icon: FileText, title: 'Estimations', desc: 'Create quotations without affecting stock' },
    { icon: Shield, title: 'Secure & Reliable', desc: 'Cloud-based with data backup' },
    { icon: Settings, title: 'Easy Setup', desc: 'Get started in minutes with guided setup' },
  ];

  const shopTypes = [
    'Fruits & Vegetable Shops', 'Grocery Stores', 'Dairy Shops', 'Footwear Stores',
    'Optical Shops', 'Agencies', 'Readymade Showrooms', 'Bakery & Tea Shops',
    'Flower Shops', 'Fancy Stores', 'Stationery Shops', 'Book Stores',
    'Ayurvedic Stores', 'Organic Stores', 'Oil Stores', 'General Retail Stores'
  ];

  const whatsappNumber = '916382947171';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=Hi, I'm interested in GPOS software`;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent" style={{ fontFamily: 'Outfit, sans-serif' }}>
                GPOS
              </span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Features</a>
              <a href="#shops" className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Who Can Use</a>
              <a href="#pricing" className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Pricing</a>
              <a href="#contact" className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Contact</a>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => navigate('/gposp/login')}
                className="px-5 py-2.5 text-violet-600 font-semibold hover:bg-violet-50 rounded-full transition-all"
                data-testid="header-login-btn"
              >
                Login
              </button>
              <button
                onClick={() => navigate('/gposp/register')}
                className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-violet-200 transition-all"
                data-testid="header-register-btn"
              >
                Register Free
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-100">
              <nav className="flex flex-col gap-3">
                <a href="#features" className="px-4 py-2 text-slate-600 hover:text-violet-600" onClick={() => setMobileMenuOpen(false)}>Features</a>
                <a href="#shops" className="px-4 py-2 text-slate-600 hover:text-violet-600" onClick={() => setMobileMenuOpen(false)}>Who Can Use</a>
                <a href="#pricing" className="px-4 py-2 text-slate-600 hover:text-violet-600" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
                <a href="#contact" className="px-4 py-2 text-slate-600 hover:text-violet-600" onClick={() => setMobileMenuOpen(false)}>Contact</a>
                <div className="flex gap-2 px-4 pt-2">
                  <button onClick={() => navigate('/gposp/login')} className="flex-1 py-2.5 text-violet-600 border border-violet-200 rounded-full font-medium">Login</button>
                  <button onClick={() => navigate('/gposp/register')} className="flex-1 py-2.5 bg-violet-600 text-white rounded-full font-medium">Register</button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-br from-violet-50 via-white to-purple-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Trusted by 1000+ Retail Stores
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                POS Billing Software for{' '}
                <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                  Small Retail Stores
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-xl mx-auto lg:mx-0">
                Simple, powerful, and affordable billing solution designed for micro and small scale retail businesses in India. GST compliant with easy-to-use interface.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() => navigate('/gposp/register')}
                  className="px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-lg font-semibold rounded-full hover:shadow-xl hover:shadow-violet-200 transition-all flex items-center justify-center gap-2"
                  data-testid="hero-register-btn"
                >
                  Start Free Trial <ChevronRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => navigate('/gposp/login')}
                  className="px-8 py-4 bg-white text-violet-600 text-lg font-semibold rounded-full border-2 border-violet-200 hover:border-violet-400 transition-all"
                >
                  Login to Dashboard
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 justify-center lg:justify-start text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-500" />
                  No Credit Card Required
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-500" />
                  14 Days Free Trial
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-violet-100 to-purple-100 rounded-3xl p-4 md:p-8 shadow-2xl">
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-violet-600 to-purple-600 p-4 text-white">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      <span className="font-semibold">GPOS Billing</span>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-slate-600">Product 1</span>
                      <span className="font-semibold">₹150.00</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-slate-600">Product 2</span>
                      <span className="font-semibold">₹280.00</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-slate-600">Product 3</span>
                      <span className="font-semibold">₹95.00</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 text-lg">
                      <span className="font-semibold text-slate-800">Total</span>
                      <span className="font-bold text-violet-600">₹525.00</span>
                    </div>
                    <button className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-semibold mt-2">
                      Complete Payment
                    </button>
                  </div>
                </div>
              </div>
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-lg p-4 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Today's Sales</p>
                    <p className="font-bold text-slate-800">₹12,450</p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg p-4 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Products</p>
                    <p className="font-bold text-slate-800">256 Items</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Powerful Features for Your Business
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to manage your retail store efficiently
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-6 bg-white rounded-2xl border border-slate-100 hover:border-violet-200 hover:shadow-xl hover:shadow-violet-100 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-violet-100 to-purple-100 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-7 h-7 text-violet-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Suitable Shops Section */}
      <section id="shops" className="py-16 md:py-24 bg-gradient-to-br from-slate-50 to-violet-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Perfect for All Retail Stores
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              GPOS is designed to work with any type of small to medium retail business
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
            {shopTypes.map((shop, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-violet-600" />
                </div>
                <span className="text-slate-700 font-medium text-sm">{shop}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose the plan that fits your business needs
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={plan.id || index}
                className={`relative p-8 rounded-3xl ${
                  index === 1
                    ? 'bg-gradient-to-br from-violet-600 to-purple-600 text-white shadow-2xl shadow-violet-200 scale-105'
                    : 'bg-white border-2 border-slate-100 hover:border-violet-200'
                } transition-all`}
              >
                {index === 1 && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 text-sm font-semibold rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className={`text-xl font-bold mb-2 ${index === 1 ? 'text-white' : 'text-slate-900'}`}>
                  {plan.name}
                </h3>
                <div className="mb-6">
                  <span className={`text-4xl font-bold ${index === 1 ? 'text-white' : 'text-slate-900'}`}>
                    ₹{plan.price_inr}
                  </span>
                  <span className={`text-sm ${index === 1 ? 'text-violet-200' : 'text-slate-500'}`}>
                    /{plan.validity_days === 30 ? 'month' : plan.validity_days + ' days'}
                  </span>
                </div>
                <ul className="space-y-3 mb-8">
                  {(plan.features || [
                    `${plan.max_products || 'Unlimited'} Products`,
                    `${plan.max_bills_per_month || 'Unlimited'} Bills/Month`,
                    'GST Invoicing',
                    'Reports & Analytics',
                    'Email Support'
                  ]).map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2">
                      <Check className={`w-5 h-5 ${index === 1 ? 'text-violet-200' : 'text-emerald-500'}`} />
                      <span className={`text-sm ${index === 1 ? 'text-violet-100' : 'text-slate-600'}`}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/gposp/register')}
                  className={`w-full py-3 rounded-full font-semibold transition-all ${
                    index === 1
                      ? 'bg-white text-violet-600 hover:shadow-lg'
                      : 'bg-violet-600 text-white hover:bg-violet-700'
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-violet-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-violet-100 mb-8">
            Join thousands of retail stores already using GPOS to manage their business efficiently.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/gposp/register')}
              className="px-8 py-4 bg-white text-violet-600 text-lg font-semibold rounded-full hover:shadow-xl transition-all"
            >
              Start Free Trial
            </button>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-emerald-500 text-white text-lg font-semibold rounded-full hover:bg-emerald-600 transition-all flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-white" />
                </div>
                <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>GPOS</span>
              </div>
              <p className="text-slate-400 mb-4">
                Empowering small retail businesses with powerful, easy-to-use POS software.
              </p>
              <p className="text-slate-400 text-sm">
                livicot.com
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-slate-400 hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="text-slate-400 hover:text-white transition-colors">Pricing</a></li>
                <li><button onClick={() => setShowPrivacy(true)} className="text-slate-400 hover:text-white transition-colors">Privacy Policy</button></li>
                <li><button onClick={() => setShowTerms(true)} className="text-slate-400 hover:text-white transition-colors">Terms & Conditions</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact & Support</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-400">
                  <Phone className="w-5 h-5 text-violet-400" />
                  <a href="tel:+916382947171" className="hover:text-white transition-colors">+91 63829 47171</a>
                </li>
                <li className="flex items-center gap-3 text-slate-400">
                  <MessageCircle className="w-5 h-5 text-emerald-400" />
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">WhatsApp Support</a>
                </li>
                <li className="flex items-center gap-3 text-slate-400">
                  <Mail className="w-5 h-5 text-violet-400" />
                  <span>info@livicot.com</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-slate-800 text-center">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} GPOS. All rights reserved. Made with ❤️ for Indian Retail Businesses.
            </p>
          </div>
        </div>
      </section>

      {/* Floating WhatsApp Button */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all z-50"
        data-testid="whatsapp-float-btn"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </a>

      {/* Privacy Policy Modal */}
      {showPrivacy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">Privacy Policy</h3>
              <button onClick={() => setShowPrivacy(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh] text-slate-600 space-y-4">
              <p><strong>Last Updated:</strong> December 2024</p>
              <p>GPOS ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information.</p>
              <h4 className="font-semibold text-slate-900">Information We Collect</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Business information (name, address, GST number)</li>
                <li>Contact information (phone number, email)</li>
                <li>Transaction data (sales, inventory, customer records)</li>
                <li>Usage data (how you interact with our software)</li>
              </ul>
              <h4 className="font-semibold text-slate-900">How We Use Your Information</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>To provide and maintain our service</li>
                <li>To process your transactions</li>
                <li>To send you updates and support communications</li>
                <li>To improve our software and services</li>
              </ul>
              <h4 className="font-semibold text-slate-900">Data Security</h4>
              <p>We implement industry-standard security measures to protect your data. All data is encrypted in transit and at rest.</p>
              <h4 className="font-semibold text-slate-900">Contact Us</h4>
              <p>For any privacy-related questions, contact us at: +91 63829 47171</p>
            </div>
          </div>
        </div>
      )}

      {/* Terms Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">Terms & Conditions</h3>
              <button onClick={() => setShowTerms(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh] text-slate-600 space-y-4">
              <p><strong>Last Updated:</strong> December 2024</p>
              <p>By using GPOS software, you agree to these Terms and Conditions.</p>
              <h4 className="font-semibold text-slate-900">1. Service Usage</h4>
              <p>GPOS provides a cloud-based POS software for retail businesses. You agree to use the service only for lawful business purposes.</p>
              <h4 className="font-semibold text-slate-900">2. Account Responsibility</h4>
              <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.</p>
              <h4 className="font-semibold text-slate-900">3. Subscription & Payments</h4>
              <p>Subscription plans are billed as per the selected plan. We offer a 14-day free trial for new users. Payments are non-refundable unless otherwise stated.</p>
              <h4 className="font-semibold text-slate-900">4. Data Ownership</h4>
              <p>You retain ownership of all data you enter into the system. We do not claim ownership over your business data.</p>
              <h4 className="font-semibold text-slate-900">5. Service Availability</h4>
              <p>We strive for 99.9% uptime but do not guarantee uninterrupted service. Maintenance windows will be communicated in advance.</p>
              <h4 className="font-semibold text-slate-900">6. Limitation of Liability</h4>
              <p>GPOS shall not be liable for any indirect, incidental, or consequential damages arising from use of the service.</p>
              <h4 className="font-semibold text-slate-900">7. Contact</h4>
              <p>For questions about these terms, contact: +91 63829 47171</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
